import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import Swal from 'sweetalert2';
import GameToolbar from '../../../components/GameToolbar/GameToolbar';
import { GAME_COPY, GAME_KEYS } from '../../../data/games';
import { apiPost } from '../../../utils/apiClient';
import { finishGame } from '../../../utils/gameSave';
import { cssColorToRgba, floodFill } from '../../../utils/floodFill';
import './RangoliPage.css';

const COLORS = [
  'red',
  'orange',
  'yellow',
  'green',
  'blue',
  'purple',
  'magenta',
  'black',
  'gray',
  'white',
];

const TOOLS = [
  { id: 'pencil', label: 'Pencil' },
  { id: 'eraser', label: 'Eraser' },
  { id: 'line', label: 'Line' },
  { id: 'circle', label: 'Circle' },
  { id: 'rectangle', label: 'Rectangle' },
  { id: 'triangle', label: 'Triangle' },
  { id: 'diamond', label: 'Diamond' },
  { id: 'star', label: 'Star' },
  { id: 'fill', label: 'Fill' },
];

const SIZES = [
  { id: 2, label: 'S' },
  { id: 4, label: 'M' },
  { id: 8, label: 'L' },
  { id: 14, label: 'XL' },
];

const SHAPE_TOOLS = new Set(['line', 'circle', 'rectangle', 'triangle', 'diamond', 'star']);

function canvasSizeForViewport() {
  if (typeof window !== 'undefined' && window.matchMedia('(min-width: 768px)').matches) {
    return { w: 800, h: 400 };
  }
  return { w: 340, h: 170 };
}

function drawStar(ctx, cx, cy, spikes, outerR, innerR) {
  let rot = (Math.PI / 2) * 3;
  const step = Math.PI / spikes;
  ctx.beginPath();
  ctx.moveTo(cx, cy - outerR);
  for (let i = 0; i < spikes; i++) {
    ctx.lineTo(cx + Math.cos(rot) * outerR, cy + Math.sin(rot) * outerR);
    rot += step;
    ctx.lineTo(cx + Math.cos(rot) * innerR, cy + Math.sin(rot) * innerR);
    rot += step;
  }
  ctx.lineTo(cx, cy - outerR);
  ctx.closePath();
}

function swalOpts(extra = {}) {
  return {
    confirmButtonText: 'Close',
    customClass: {
      confirmButton: 'hf-swal-close',
      popup: 'hf-swal-popup',
    },
    buttonsStyling: false,
    ...extra,
  };
}

export default function RangoliPage() {
  const navigate = useNavigate();
  const user = useSelector((s) => s.auth.user);
  const canvasRef = useRef(null);
  const drawing = useRef(false);
  const startPt = useRef(null);
  const snapshot = useRef(null);
  const history = useRef([]);
  const redoStack = useRef([]);

  const [color, setColor] = useState('black');
  const [customColor, setCustomColor] = useState('#e25569');
  const [brush, setBrush] = useState('pencil');
  const [brushSize, setBrushSize] = useState(4);
  const [fillShapes, setFillShapes] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [posts, setPosts] = useState([]);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [canvasSize, setCanvasSize] = useState(canvasSizeForViewport);
  const limit = 9;

  const getCtx = () => canvasRef.current?.getContext('2d');

  const pushHistory = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    history.current.push(canvas.toDataURL());
    if (history.current.length > 40) history.current.shift();
    redoStack.current = [];
  };

  const restoreDataUrl = (url) => {
    const canvas = canvasRef.current;
    const ctx = getCtx();
    if (!canvas || !ctx || !url) return;
    const img = new Image();
    img.onload = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
    };
    img.src = url;
  };

  const initCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    const ctx = getCtx();
    if (!canvas || !ctx) return;
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    history.current = [];
    redoStack.current = [];
    pushHistory();
  }, []);

  useEffect(() => {
    initCanvas();
  }, [canvasSize, initCanvas]);

  useEffect(() => {
    const mq = window.matchMedia('(min-width: 768px)');
    const onChange = () => {
      const next = canvasSizeForViewport();
      setCanvasSize((prev) => (prev.w === next.w ? prev : next));
    };
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  const loadGallery = useCallback(
    async (p = 1) => {
      if (!user?.userId) return;
      try {
        const res = await apiPost('/rangoli/gallery', {
          userId: user.userId,
          page: p,
          limit,
        });
        setPosts(res.posts || []);
        setTotal(res.total || 0);
        setPage(p);
      } catch (err) {
        console.error(err);
      }
    },
    [user]
  );

  useEffect(() => {
    loadGallery(1);
  }, [loadGallery]);

  const pointerPos = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const clientX = e.clientX ?? e.touches?.[0]?.clientX;
    const clientY = e.clientY ?? e.touches?.[0]?.clientY;
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    return {
      x: (clientX - rect.left) * scaleX,
      y: (clientY - rect.top) * scaleY,
    };
  };

  const applyFillBucket = (pt) => {
    const canvas = canvasRef.current;
    const ctx = getCtx();
    if (!canvas || !ctx) return;
    const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
    // High tolerance + expand: fill meets anti-aliased star/circle strokes
    floodFill(imageData, pt.x, pt.y, cssColorToRgba(color), 160, 2);
    ctx.putImageData(imageData, 0, 0);
    pushHistory();
  };

  const drawShape = (ctx, s, pt) => {
    const w = pt.x - s.x;
    const h = pt.y - s.y;
    const cx = s.x + w / 2;
    const cy = s.y + h / 2;

    if (brush === 'line') {
      ctx.beginPath();
      ctx.moveTo(s.x, s.y);
      ctx.lineTo(pt.x, pt.y);
      ctx.stroke();
      return;
    }

    if (brush === 'circle') {
      const rx = Math.abs(w) / 2 || 1;
      const ry = Math.abs(h) / 2 || 1;
      ctx.beginPath();
      ctx.ellipse(cx, cy, rx, ry, 0, 0, Math.PI * 2);
    } else if (brush === 'rectangle') {
      ctx.beginPath();
      ctx.rect(s.x, s.y, w, h);
    } else if (brush === 'triangle') {
      ctx.beginPath();
      ctx.moveTo(cx, s.y);
      ctx.lineTo(s.x, s.y + h);
      ctx.lineTo(s.x + w, s.y + h);
      ctx.closePath();
    } else if (brush === 'diamond') {
      ctx.beginPath();
      ctx.moveTo(cx, s.y);
      ctx.lineTo(s.x + w, cy);
      ctx.lineTo(cx, s.y + h);
      ctx.lineTo(s.x, cy);
      ctx.closePath();
    } else if (brush === 'star') {
      const outerR = Math.max(Math.abs(w), Math.abs(h)) / 2 || 1;
      drawStar(ctx, cx, cy, 5, outerR, outerR * 0.45);
    } else {
      return;
    }

    if (fillShapes) ctx.fill();
    ctx.stroke();
  };

  const onDown = (e) => {
    e.preventDefault();
    const ctx = getCtx();
    const canvas = canvasRef.current;
    if (!ctx || !canvas) return;
    const pt = pointerPos(e);

    if (brush === 'fill') {
      applyFillBucket(pt);
      return;
    }

    drawing.current = true;
    startPt.current = pt;
    snapshot.current = ctx.getImageData(0, 0, canvas.width, canvas.height);

    if (brush === 'eraser') {
      ctx.strokeStyle = '#ffffff';
      ctx.fillStyle = '#ffffff';
      ctx.lineWidth = brushSize * 2.2;
    } else {
      ctx.strokeStyle = color;
      ctx.fillStyle = color;
      ctx.lineWidth = brush === 'pencil' ? brushSize : Math.max(3, brushSize);
    }
    ctx.lineCap = brush === 'pencil' || brush === 'eraser' ? 'round' : 'butt';
    ctx.lineJoin = brush === 'pencil' || brush === 'eraser' ? 'round' : 'miter';
    ctx.miterLimit = 3;

    if (brush === 'pencil' || brush === 'eraser') {
      ctx.beginPath();
      ctx.moveTo(pt.x, pt.y);
    }
  };

  const onMove = (e) => {
    if (!drawing.current || brush === 'fill') return;
    e.preventDefault();
    const ctx = getCtx();
    if (!ctx) return;
    const pt = pointerPos(e);

    if (brush === 'pencil' || brush === 'eraser') {
      ctx.lineTo(pt.x, pt.y);
      ctx.stroke();
      return;
    }

    if (SHAPE_TOOLS.has(brush)) {
      if (snapshot.current) ctx.putImageData(snapshot.current, 0, 0);
      ctx.strokeStyle = color;
      ctx.fillStyle = color;
      ctx.lineWidth = Math.max(3, brushSize);
      drawShape(ctx, startPt.current, pt);
    }
  };

  const onUp = (e) => {
    if (!drawing.current) return;
    e.preventDefault();
    drawing.current = false;
    pushHistory();
  };

  const undo = () => {
    if (history.current.length < 2) return;
    const current = history.current.pop();
    redoStack.current.push(current);
    restoreDataUrl(history.current[history.current.length - 1]);
  };

  const redo = () => {
    const next = redoStack.current.pop();
    if (!next) return;
    history.current.push(next);
    restoreDataUrl(next);
  };

  const onSubmit = async () => {
    if (!user || submitting) return;
    setSubmitting(true);
    try {
      const dataURL = canvasRef.current.toDataURL('image/png');
      const upload = await apiPost('/rangoli/saveImage', {
        userId: user.userId,
        name: user.name,
        email: user.email,
        image: dataURL,
      });
      if (upload.code !== '1010') {
        throw new Error(upload.error || 'Upload failed');
      }
      const result = await finishGame({
        user,
        gameKey: GAME_KEYS.rangoli,
        score: 10,
        elapsedSeconds: 0,
      });
      if (result.alreadyPlayed) {
        navigate('/hub');
      } else {
        navigate(`/thank-you?game=${GAME_KEYS.rangoli}&points=${result.coinsEarned ?? 0}`);
      }
    } catch (err) {
      console.error(err);
      Swal.fire(swalOpts({
        title: 'Upload failed',
        text: err.message || 'Please try again',
        icon: 'error',
      }));
      setSubmitting(false);
    }
  };

  const onLike = async (post) => {
    if (!user) return;
    if (post.liked) {
      await Swal.fire(swalOpts({
        imageUrl: '/rangoli/ohno.png',
        imageWidth: 80,
        text: 'You already liked this post!',
      }));
      return;
    }
    try {
      const res = await apiPost('/rangoli/like', {
        userId: user.userId,
        postId: post.id,
      });
      if (res.code === 'blocked') {
        await Swal.fire(swalOpts({
          imageUrl: '/rangoli/ohno.png',
          imageWidth: 80,
          text: 'You already liked this post!',
        }));
        return;
      }
      await Swal.fire(swalOpts({
        imageUrl: '/rangoli/popup-like.png',
        imageWidth: 80,
        text: 'Thank you for liking!',
      }));
      loadGallery(page);
    } catch (err) {
      console.error(err);
    }
  };

  const copy = GAME_COPY.rangoli;
  const pages = Math.max(1, Math.ceil(total / limit));

  return (
    <div className="rangoli-page bg-games" style={{ '--page-bg': 'url(/img/games-background.jpg)' }}>
      <GameToolbar rules={copy.rules} />
      <p className="rangoli-title">{copy.title}</p>

      <div className="rangoli-tools">
        <ul className="rangoli-colors">
          {COLORS.map((c) => (
            <li
              key={c}
              title={c}
              className={color === c ? 'selected' : ''}
              style={{ backgroundColor: c }}
              onClick={() => setColor(c)}
            />
          ))}
          <li
            className={`rangoli-custom-color ${color === customColor ? 'selected' : ''}`}
            title="Custom color"
          >
            <input
              type="color"
              value={customColor}
              aria-label="Custom color"
              onChange={(e) => {
                const next = e.target.value;
                setCustomColor(next);
                setColor(next);
              }}
              onClick={(e) => e.stopPropagation()}
            />
          </li>
        </ul>
        <ul className="rangoli-brushes">
          {TOOLS.map((b) => (
            <li
              key={b.id}
              className={brush === b.id ? 'selected' : ''}
              onClick={() => setBrush(b.id)}
            >
              {b.label}
            </li>
          ))}
        </ul>
        <ul className="rangoli-sizes">
          {SIZES.map((s) => (
            <li
              key={s.id}
              className={brushSize === s.id ? 'selected' : ''}
              onClick={() => setBrushSize(s.id)}
              title={`Size ${s.label}`}
            >
              {s.label}
            </li>
          ))}
        </ul>
        <label className="rangoli-fill-toggle">
          <input
            type="checkbox"
            checked={fillShapes}
            onChange={(e) => setFillShapes(e.target.checked)}
          />
          Fill shapes
        </label>
        <ul className="rangoli-stack">
          <li onClick={undo}>Undo</li>
          <li onClick={redo}>Redo</li>
        </ul>
      </div>

      <div className="rangoli-canvas-wrap">
        <canvas
          ref={canvasRef}
          className="rangoli-canvas"
          width={canvasSize.w}
          height={canvasSize.h}
          onPointerDown={onDown}
          onPointerMove={onMove}
          onPointerUp={onUp}
          onPointerLeave={onUp}
        />
      </div>

      <button
        type="button"
        className="rangoli-submit"
        onClick={onSubmit}
        disabled={submitting}
      >
        {submitting ? 'Submitting…' : 'Submit'}
      </button>

      <div className="rangoli-gallery">
        {posts.length === 0 ? (
          <div className="rangoli-empty">No Post. Be the first one.</div>
        ) : (
          posts.map((post) => (
            <div className="rangoli-card" key={post.id}>
              <img className="post" src={post.imageUrl} alt={post.name || 'Rangoli'} />
              <div className="rangoli-card-meta">
                <button type="button" className="rangoli-like" onClick={() => onLike(post)}>
                  <img src="/rangoli/like_new.png" alt="" />
                  <span>{post.likes}</span>
                </button>
                <span>{post.name || 'User'}</span>
              </div>
            </div>
          ))
        )}
      </div>

      {pages > 1 && (
        <div className="rangoli-pager">
          <button type="button" disabled={page <= 1} onClick={() => loadGallery(page - 1)}>
            Previous
          </button>
          <button type="button" disabled={page >= pages} onClick={() => loadGallery(page + 1)}>
            Next
          </button>
        </div>
      )}
    </div>
  );
}
